import {
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spacer,
  Text,
  useColorMode,
  useToast,
  VStack
} from "@chakra-ui/react"
import React, { useState } from "react"

import Pathicon, { iconNames } from "./Pathicon"

const ColorPicker = ({ value, onChange }) => {
  const handleChange = e => {
    onChange(e.target.value)
  }

  return (
    <HStack>
      <Text color="gray" fontSize="sm" fontWeight="bold">
        Color
      </Text>
      <Box boxSize="25px" borderRadius="100%" bg={value} pos="relative">
        <Input
          type="color"
          name="color"
          value={value}
          onChange={handleChange}
          pos="absolute"
          inset="0px"
          opacity="0"
        />
      </Box>
    </HStack>
  )
}

const SizeSlider = ({ value, onChange }) => {
  const handleChange = value => {
    onChange(value)
  }

  return (
    <HStack w={"full"}>
      <Text color="gray" fontSize="sm" fontWeight="bold">
        Size
      </Text>
      <Slider value={value} onChange={handleChange} min={10} max={120} step={2}>
        <SliderTrack>
          <Box position="relative" right={10} />
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb boxSize={6} />
      </Slider>
      <Text color="gray" fontSize="sm">
        {value}px
      </Text>
    </HStack>
  )
}

const DarkModeToggle = () => {
  const { toggleColorMode, colorMode } = useColorMode()

  return (
    <IconButton
      aria-label="Toggle dark mode"
      size={"sm"}
      onClick={toggleColorMode}
      icon={<Pathicon size={14} name={colorMode === "dark" ? "sun" : "moon"} />}
      pos="absolute"
      top={2}
      right={2}
    />
  )
}

const App = () => {
  const [search, setSearch] = useState("")
  const [size, setSize] = useState(24)
  const [color, setColor] = useState("gray")

  const toast = useToast()

  const handleSearch = e => {
    setSearch(e.target.value)
  }

  const handleCopyIcon = str => {
    const el = document.createElement("textarea")
    el.value = str
    el.setAttribute("readonly", "")
    el.style.position = "absolute"
    el.style.left = "-9999px"
    document.body.appendChild(el)
    el.select()
    document.execCommand("copy")
    document.body.removeChild(el)

    toast({
      title: "Copied to clipboard",
      description: str,
      status: "success",
      isClosable: true,
      duration: 2000,
      icon: <Pathicon name="check" />
    })
  }

  const handleDownloadIcon = name => async event => {
    event.preventDefault()
    event.stopPropagation()

    try {
      // Dynamically import the SVG file as a module
      const svgModule = await import(`!!raw-loader!../../src/svg/${name}.svg`)
      const blob = new Blob([svgModule.default], { type: "image/svg+xml" })

      // Create a download link for the blob
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = `${name}.svg` // Download the SVG file with the given name
      link.click() // Trigger the download

      // Clean up the object URL
      URL.revokeObjectURL(link.href)
      console.log("Downloaded", name)
    } catch (error) {
      console.error("Error downloading SVG:", error)
    }
  }

  let visibleIcons = iconNames
  let iconCount = iconNames.length
  if (search) {
    visibleIcons = visibleIcons.filter(name => {
      return name.includes(search.toLowerCase())
    })
  }

  return (
    <Box w={"full"} height={"full"} p={4}>
      <DarkModeToggle />
      <VStack spacing={6}>
        <Heading>Pathicons</Heading>
        <HStack w={"full"}>
          <InputGroup>
            <InputLeftElement
              pointerEvents="none"
              children={<Pathicon name="search" color="gray" size={12} />}
            />
            <Input
              value={search}
              onChange={handleSearch}
              placeholder={`Search all ${iconCount} icons...`}
            />
          </InputGroup>
          <Box w={"200px"}>
            <SizeSlider value={size} onChange={setSize} />
          </Box>
          <Divider orientation="vertical" h="25px" />
          <ColorPicker value={color} onChange={setColor} />
        </HStack>

        <SimpleGrid w={"full"} minChildWidth="160px" spacing={4}>
          {visibleIcons.map(name => {
            return (
              <Button
                as={VStack}
                // flexShrink={0}
                height={"auto"}
                borderWidth={1}
                position="relative"
                rounded="2xl"
                maxWidth={"180px"}
                p={4}
                aria-label={`${name} icon`}
                key={name}
                onClick={() => handleCopyIcon(name)}
                _hover={{
                  button: {
                    opacity: 1
                  }
                }}
              >
                <IconButton
                  aria-label={`Download ${name} icon`}
                  icon={
                    <Pathicon name="arrow-down" size="0.9em" color={color} />
                  }
                  onClick={handleDownloadIcon(name)}
                  opacity={0}
                  pos="absolute"
                  p={1}
                  top={1}
                  right={1}
                  h={7}
                  w={7}
                  minW={7}
                  transition="opacity 0.1s"
                  borderRadius="50%"
                  bg="gray.100"
                />
                <Pathicon size={size} color={color} name={name} />
                <Spacer h={4} />
                <Text fontSize={"xs"}>{name}</Text>
              </Button>
            )
          })}
        </SimpleGrid>
      </VStack>
    </Box>
  )
}

export default App
